import { SyntheticEvent, useRef } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Block from '../../components/Block';
import Column from '../../components/Column';
import Heading from '../../components/Heading';
import Row from '../../components/Row';
import { Dealer as Advertiser } from '../../types/Dealer';
import { PrintContext } from '../../hooks/usePrint';
import {
    ReportingDashboardPageContextValue,
    ReportingDashboardPageProvider,
} from '../../hooks/useReportingDashboardPage';
import { useReportingDashboardPage } from '../../hooks/useReportingDashboardPage';
import usePrint from '../../hooks/usePrint';
import ReportingCriteriaForm from './partials/Reporting/ReportingCriteriaForm';
import ReportingMediaTabs, {
    DEFAULT_TAB_SLUG,
    REPORTING_TAB_CONFIG as MEDIA_TABS_CONFIG,
    isReportingTabSlug,
} from './partials/Reporting/Tabs/ReportingMediaTabs';
import ReportingPerformanceTabPanels from './partials/Reporting/Tabs/ReportingPerformanceTabPanels';

import ReportingAdvertiserCountLabel from './partials/Reporting/ReportingAdvertiserCountLabel';
import ReportingMediaTypeTabAlert from './partials/Reporting/Alerts/ReportingMediaTypeTabAlert';
import ReportingPerformanceExportMenuButton from './partials/Reporting/ReportingPerformanceExportMenuButton';
import ReportingPerformanceMetrics from './partials/Reporting/ReportingPerformanceMetrics';
import { AttributionPageProvider, useAttributionPage } from '../../hooks/useAttributionPage';
import usePageTitle from '../../hooks/usePageTitle';
import { ReportingCriteria } from '../../types/Criteria/ReportingCriteria';
import ReportingAllMediaTypeTabAlert from './partials/Reporting/Alerts/ReportingAllMediaTypeTabAlert';

interface ReportingDashboardPageNavigation {
    performanceTab?: number;
    reportingCriteria?: ReportingCriteria;
}

const useReportingDashboardPageNavigator = (context: ReportingDashboardPageContextValue) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const { mediaTabs, performanceTabs, setReportingCriteria } = context;

    // Prevent double-clearing / loops
    const consumedOnce = useRef<boolean>(false);

    useEffect(() => {
        const nav = (location.state ?? null) as ReportingDashboardPageNavigation | null;
        if (!nav || consumedOnce.current) return;

        // apply incoming state
        if (nav.performanceTab !== undefined) {
            performanceTabs.setActiveTab(nav.performanceTab);
        }
        if (nav.reportingCriteria) {
            setReportingCriteria((prev) => ({ ...prev, ...nav.reportingCriteria }));
        }

        // Clear it so refresh/back won't restore it
        consumedOnce.current = true;
        navigate(location.pathname, { replace: true, state: null });
    }, [location.state, location.pathname, navigate, performanceTabs, setReportingCriteria]);

    useEffect(() => {
        consumedOnce.current = false;
    }, [location.pathname]);

    // Handle URL changes & update tab state (e.g., browser back/forward) ----
    useEffect(() => {
        if (!params.tab || !isReportingTabSlug(params.tab)) {
            navigate(`/reporting/${DEFAULT_TAB_SLUG}`, { replace: true });
            return;
        }

        const selectedTabIndex = MEDIA_TABS_CONFIG[params.tab].tabIndex;

        if (mediaTabs.activeTab !== selectedTabIndex) {
            mediaTabs.setActiveTab(selectedTabIndex);
        }

        let mediaType = '';

        switch (params.tab) {
            case 'display':
                mediaType = 'Display';
                break;
            case 'audio':
                mediaType = 'Audio';
                break;
            case 'online-video':
                mediaType = 'Online Video';
                break;
            case 'streaming-tv':
                mediaType = 'Streaming TV';
                break;
            case 'video':
                mediaType = 'Video';
                break;
            default:
                mediaType = '';
        }

        // Only update criteria when a tab is changed;
        setReportingCriteria((prev) => {
            if (prev.mediaType === mediaType) {
                return prev;
            }
            return {
                ...prev,
                mediaType,
            };
        });
    }, [params.tab]);

    return {};
};

export default function ReportingDashboardPage() {
    const theme = useTheme();
    const print = usePrint();
    const navigate = useNavigate();
    const params = useParams();
    const reportingDashboardPage = useReportingDashboardPage();
    const attributionPage = useAttributionPage();

    const {
        activeMediaTypes,
        isActiveMediaType,
        isFetchingPerformances,
        performances,
        reportingCriteria,
        setReportingCriteria,
        mediaTabs,
    } = reportingDashboardPage;

    useReportingDashboardPageNavigator(reportingDashboardPage);

    const [activeAdvertiserCount, setActiveAdvertiserCount] = useState<number>(0);
    const [totalAdvertiserCount, setTotalAdvertiserCount] = useState<number>(0);
    const [totalActiveAdvertiserCount, setTotalActiveAdvertiserCount] = useState<number>(0);

    // Derive current tab from URL (single source of truth)
    const currentTabSlug = params.tab && isReportingTabSlug(params.tab) ? params.tab : DEFAULT_TAB_SLUG;
    const tabLabel = MEDIA_TABS_CONFIG[currentTabSlug].label;
    usePageTitle({ segments: ['Reporting', tabLabel] });

    const handleChangeMediaTab = useCallback(
        (event: SyntheticEvent, newActiveTab: number) => {
            mediaTabs.handleChangeTab(event, newActiveTab);

            // Navigate to the corresponding URL slug
            const tabSlug = Object.values(MEDIA_TABS_CONFIG).find((config) => config.tabIndex === newActiveTab)?.slug;

            if (tabSlug) {
                navigate(`/reporting/${tabSlug}`);
            }

            let mediaType: string = '';

            switch (newActiveTab) {
                case MEDIA_TABS_CONFIG['all-media'].tabIndex:
                    mediaType = '';
                    break;
                case MEDIA_TABS_CONFIG.display.tabIndex:
                    mediaType = 'Display';
                    break;
                case MEDIA_TABS_CONFIG.audio.tabIndex:
                    mediaType = 'Audio';
                    break;
                case MEDIA_TABS_CONFIG['online-video'].tabIndex:
                    mediaType = 'Online Video';
                    break;
                case MEDIA_TABS_CONFIG['streaming-tv'].tabIndex:
                    mediaType = 'Streaming TV';
                    break;
                // Note: Legacy media type
                case MEDIA_TABS_CONFIG.video.tabIndex:
                    mediaType = 'Video';
                    break;
            }

            setReportingCriteria((prev) => ({
                ...prev,
                mediaType: mediaType,
            }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mediaTabs.setActiveTab, navigate]
    );

    const showMediaTypeTabAlert = useMemo((): boolean => {
        // if (reportingCriteria.mediaType === 'Video') {
        //     return false;
        // }
        return (
            !isFetchingPerformances &&
            reportingCriteria.mediaType.length > 0 &&
            !isActiveMediaType(reportingCriteria.mediaType)
        );
    }, [reportingCriteria.mediaType, isActiveMediaType, isFetchingPerformances]);

    const showAllMediaTypeTabAlert = useMemo((): boolean => {
        return (
            !isFetchingPerformances &&
            !showMediaTypeTabAlert &&
            reportingCriteria.mediaType.length === 0 &&
            Object.entries(activeMediaTypes).length === 0
        );
    }, [reportingCriteria.mediaType, activeMediaTypes, showMediaTypeTabAlert, isFetchingPerformances]);

    const isFetchingAdvertiserCount = useMemo(() => {
        return isFetchingPerformances;
    }, [isFetchingPerformances]);

    const setupAdvertiserCounts = useCallback(() => {
        const distinctAdvertiserIds = [...new Set(performances.map((performance: any) => performance.dealerId))];
        setActiveAdvertiserCount(distinctAdvertiserIds.length);

        if (totalActiveAdvertiserCount > 0) {
            setTotalAdvertiserCount(totalActiveAdvertiserCount);
        } else {
            setTotalAdvertiserCount(distinctAdvertiserIds.length);
        }

        if (reportingCriteria.dealerIds.length > 0) {
            setTotalAdvertiserCount(reportingCriteria.dealerIds.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportingCriteria.dealerIds, performances, totalActiveAdvertiserCount]);

    useEffect(() => {
        setupAdvertiserCounts();
    }, [setupAdvertiserCounts]);

    return (
        <PrintContext.Provider value={print}>
            <ReportingDashboardPageProvider value={reportingDashboardPage}>
                <Column gap={0} flex={1}>
                    <Block>
                        <Column gap={1}>
                            <Row spacing={2} justifyContent="space-between" alignItems="center">
                                <Heading>Reporting</Heading>

                                <Row justifyContent="flex-end"></Row>
                            </Row>
                        </Column>
                    </Block>

                    <Block py={0}>
                        <Row spacing={2} justifyContent="space-between" alignItems="center">
                            <Row spacing={2} alignItems="center">
                                <Box
                                    sx={{
                                        '&': {
                                            width: '100%',

                                            [theme.breakpoints.up('xl')]: {
                                                width: 1200,
                                            },
                                        },
                                    }}
                                >
                                    <ReportingCriteriaForm
                                        reportingCriteria={reportingCriteria}
                                        onChangeReportingCriteria={setReportingCriteria}
                                        onFetchAdvertisers={(advertisers: Advertiser[]): void => {
                                            setTotalActiveAdvertiserCount(
                                                advertisers.filter(
                                                    (advertiser: { activeStatus: boolean }) => advertiser.activeStatus
                                                ).length
                                            );
                                        }}
                                    />
                                </Box>
                            </Row>
                        </Row>
                    </Block>

                    <Block disableGutters>
                        <ReportingMediaTabs value={mediaTabs.activeTab} onChange={handleChangeMediaTab} />
                    </Block>

                    <Block>
                        <Column>
                            {showAllMediaTypeTabAlert && <ReportingAllMediaTypeTabAlert />}
                            {showMediaTypeTabAlert && (
                                <ReportingMediaTypeTabAlert mediaType={reportingCriteria.mediaType} />
                            )}

                            <Row justifyContent="space-between">
                                <ReportingAdvertiserCountLabel
                                    loading={isFetchingAdvertiserCount}
                                    activeAdvertiserCount={activeAdvertiserCount}
                                    totalAdvertiserCount={totalAdvertiserCount}
                                />

                                <AttributionPageProvider value={attributionPage}>
                                    <ReportingPerformanceExportMenuButton disabled={false} />
                                </AttributionPageProvider>
                            </Row>
                        </Column>
                    </Block>

                    <Block>
                        <Column>
                            <Box>
                                <ReportingPerformanceMetrics />
                            </Box>
                        </Column>

                        <Column>
                            <ReportingPerformanceTabPanels />
                        </Column>
                    </Block>
                </Column>
            </ReportingDashboardPageProvider>
        </PrintContext.Provider>
    );
}
